/* No size until the student picks one.

   Impact renders the first available variant as selected, so a product page
   and a quick buy drawer both open with XS checked and "Legg i handlekurv"
   live. In a school order that is the likeliest wrong size there is: a
   student who never looks at the size row gets an XS. Two orders in, that
   was already the pattern worth closing.

   The size radios carry `required` (snippets/option-value.liquid, set by
   snippets/variant-picker.liquid on the size option), so with none checked
   the form fails its own validity check: ProductForm._onSubmit in theme.js
   stops at checkValidity() and reportValidity(), and add-to-cart-guard.js
   sees the same answer and arms nothing. This file does the rest:

   - unchecks the size radios on every render until the student has clicked
     one. Every render, because the picker fetches the section again on each
     option change and Shopify marks a size on every one of them, so a colour
     change alone would put XS back. The element below is rendered inside the
     size fieldset and rebuilt with it, which is how it gets to run each time.
   - remembers the choice per form id for the life of the page, so a colour
     change after the size is picked keeps the size.
   - counts a ?variant= in the page URL as a choice: a cart line links to its
     own variant, and a reload after picking keeps what was picked. Impact
     also writes ?variant= after a colour-only change (update-url), which is
     no choice, so that one is taken back off the URL.
   - shows the line "Velg en størrelse først." when the button is pressed with
     no size, on the browser's `invalid` event, beside the pills. */
(function () {
  "use strict";

  /* Form ids whose size the student has clicked. Module level on purpose:
     the picker rerenders the section on every option change and the drawer
     is rebuilt on every open, and the element below goes with them. */
  var chosen = new Set();

  /* Read once, at script time. The check has to predate Impact's own
     replaceState, which lands ?variant= on the URL after a colour-only
     change and would otherwise read as a choice on the very next render. */
  var arrivedWithVariant = false;
  try {
    arrivedWithVariant = new URL(window.location.href).searchParams.has("variant");
  } catch (e) {}

  /* form.id is the hidden variant field, not the attribute
     (HTMLFormElement is [LegacyOverrideBuiltIns]). */
  function formId(form) {
    return form ? form.getAttribute("id") : null;
  }

  /* The size radios of one form. form.elements includes controls associated
     through the form attribute, which is how the picker's inputs are tied to
     a form they do not sit inside. */
  function sizeRadios(form, position) {
    return Array.prototype.filter.call(form.elements, function (el) {
      return (
        el.tagName === "INPUT" &&
        el.type === "radio" &&
        el.getAttribute("data-option-position") === position
      );
    });
  }

  function choiceFor(form, position) {
    var id = formId(form);
    if (!id) return null;
    return document.querySelector(
      'size-choice[form-id="' + id + '"][option-position="' + position + '"]'
    );
  }

  class SizeChoice extends HTMLElement {
    connectedCallback() {
      var id = this.getAttribute("form-id");
      var position = this.getAttribute("option-position");
      var form = id && document.forms[id];
      if (!form || !position) return;

      this.form = form;
      this.position = position;
      this.fieldset = this.closest("fieldset");
      this.banner = this.fieldset && this.fieldset.querySelector("[data-size-banner]");

      /* The page's own product form, arrived at through a link that names a
         variant: that link chose. Not a quick buy drawer on that page, which
         has had nothing chosen in it. */
      if (arrivedWithVariant && !this.closest("quick-buy-drawer")) chosen.add(id);

      if (chosen.has(id)) return;

      sizeRadios(form, position).forEach(function (input) {
        input.checked = false;
      });

      /* "Størrelse: XS" would still name the size just unchecked. */
      var value = this.fieldset && this.fieldset.querySelector("[data-size-value]");
      if (value) value.textContent = "";
    }

    showBanner() {
      if (!this.banner) return;
      this.banner.hidden = false;
      if (this.fieldset && this.fieldset.scrollIntoView) {
        this.fieldset.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    }

    hideBanner() {
      if (this.banner) this.banner.hidden = true;
    }
  }

  /* A click on a size pill. The picker will rerender the section from this
     change, and the new fieldset's element connects with the choice on
     record.

     Capture phase, and it has to be. Impact's picker listens for the same
     change on body, and when it already holds the section's HTML (it
     preloads every combination) its await resolves in the microtask
     checkpoint between listeners: the swap has happened before a bubbling
     listener on document is reached, the radio is out of the document and
     its .form is null. Capture runs before body sees anything. */
  document.addEventListener(
    "change",
    function (event) {
      var input = event.target;
      if (!input || input.tagName !== "INPUT" || input.type !== "radio") return;
      var position = input.getAttribute("data-option-position");
      if (!position || !input.form) return;
      var choice = choiceFor(input.form, position);
      if (!choice) return;
      chosen.add(formId(input.form));
      choice.hideBanner();
    },
    true
  );

  /* The button pressed with no size. `invalid` does not bubble, hence capture.
     reportValidity() raises it on every radio of the group, and showing the
     line twice is showing it once. preventDefault() keeps the browser's own
     bubble ("Please select one of these options", in the browser's language,
     anchored to a radio that is visually hidden) off the screen: the line in
     the fieldset says it, in Norwegian, where the pills are. */
  document.addEventListener(
    "invalid",
    function (event) {
      var input = event.target;
      if (!input || input.tagName !== "INPUT" || input.type !== "radio") return;
      var position = input.getAttribute("data-option-position");
      if (!position || !input.form) return;
      var choice = choiceFor(input.form, position);
      if (!choice) return;
      event.preventDefault();
      choice.showBanner();
    },
    true
  );

  /* Impact's update-url writes ?variant= for every variant change, colour
     included. With no size chosen that URL names a size nobody picked, and a
     reload would show it checked. Take it back off. */
  document.addEventListener("variant:change", function (event) {
    var form = event.target;
    if (!form || form.tagName !== "FORM") return;
    if (chosen.has(formId(form))) return;
    try {
      var url = new URL(window.location.href);
      if (!url.searchParams.has("variant")) return;
      url.searchParams.delete("variant");
      window.history.replaceState(window.history.state, "", url.toString());
    } catch (e) {}
  });

  if (!customElements.get("size-choice")) {
    customElements.define("size-choice", SizeChoice);
  }
})();
